import PageLayout from "../../components/servicing/layout/PageLayout";
import { Link } from "react-router-dom";
import {
  ShoppingCartIcon,
  ArrowLeftIcon,
  SparklesIcon,
  TruckIcon,
  ShieldCheckIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const Cart = () => {
  const suggestions = [
    {
      icon: SparklesIcon,
      title: "Basic Service",
      description: "Complete bike check-up and maintenance",
      price: "₹499"
    },
    {
      icon: TruckIcon,
      title: "Premium Service",
      description: "Comprehensive servicing with warranty",
      price: "₹999"
    },
    {
      icon: ShieldCheckIcon,
      title: "Repair Service",
      description: "Expert repairs for all bike issues",
      price: "₹799"
    }
  ];

  const benefits = [
    {
      icon: ShieldCheckIcon,
      title: "Quality Guaranteed",
      description: "All services come with warranty"
    },
    {
      icon: ClockIcon,
      title: "Quick Turnaround",
      description: "Most services completed in 24hrs"
    },
    {
      icon: TruckIcon,
      title: "Free Pickup",
      description: "Doorstep service available"
    }
  ];

  return (
    <PageLayout>
      <div className="min-h-[calc(100vh-160px)] bg-gradient-to-br from-gray-50 via-white to-indigo-50">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          {/* Empty Cart State */}
          <div className="bg-white rounded-3xl shadow-xl border border-gray-200 overflow-hidden">
            {/* Header Section */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-8 px-6 text-center">
              <div className="flex justify-center mb-4">
                <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center">
                  <ShoppingCartIcon className="h-10 w-10" />
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">Your Cart</h1>
              <p className="text-indigo-100 text-lg">Ready to book your vehicle service?</p>
            </div>

            {/* Empty State Content */}
            <div className="p-8 md:p-12">
              <div className="text-center mb-12">
                <div className="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <ShoppingCartIcon className="h-16 w-16 text-gray-400" />
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  Your Cart is Empty
                </h2>
                <p className="text-gray-600 text-lg mb-8 max-w-md mx-auto">
                  Looks like you haven't added any services yet. Let's get your vehicle serviced!
                </p>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                  <Link 
                    to="/" 
                    className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-lg font-semibold rounded-xl hover:shadow-2xl transition-all duration-300 hover:scale-105"
                  >
                    <span>Browse Services</span>
                    <svg className="h-5 w-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>

                  <Link 
                    to="/" 
                    className="inline-flex items-center gap-2 px-8 py-4 bg-white border-2 border-gray-300 text-gray-700 text-lg font-semibold rounded-xl hover:border-indigo-500 hover:text-indigo-600 transition-all duration-300"
                  >
                    <ArrowLeftIcon className="h-5 w-5" />
                    <span>Go to Home</span>
                  </Link>
                </div>
              </div>

              {/* Divider */}
              <div className="relative my-12">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500 font-medium">Explore Our Services</span>
                </div>
              </div>

              {/* Service Suggestions */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                  Popular Services
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {suggestions.map((service, index) => {
                    const Icon = service.icon;
                    return (
                      <div
                        key={index}
                        className="group relative bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border-2 border-gray-200 hover:border-indigo-500 hover:shadow-xl transition-all duration-300"
                      >
                        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 to-purple-50 opacity-0 group-hover:opacity-100 rounded-2xl transition-opacity"></div>
                        <div className="relative">
                          <div className="w-14 h-14 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform shadow-lg">
                            <Icon className="h-7 w-7 text-white" />
                          </div>
                          <h4 className="text-lg font-bold text-gray-900 mb-2">
                            {service.title}
                          </h4>
                          <p className="text-sm text-gray-600 mb-4">
                            {service.description}
                          </p>
                          <div className="flex items-center justify-between">
                            <span className="text-2xl font-bold text-indigo-600">
                              {service.price}
                            </span>
                            <Link
                              to="/"
                              className="text-sm font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                            >
                              View Details
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Why Choose Us */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-8 border border-indigo-200">
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-8">
                  Why Choose Ve go?
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {benefits.map((benefit, index) => {
                    const Icon = benefit.icon;
                    return (
                      <div key={index} className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                            <Icon className="h-6 w-6 text-white" />
                          </div>
                        </div>
                        <div>
                          <h4 className="text-lg font-bold text-gray-900 mb-1">
                            {benefit.title}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Additional CTA Section */}
          <div className="mt-12 text-center">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
              <p className="text-gray-600 mb-4">
                Need help choosing a service? Our experts are here to assist!
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <a
                  href="tel:+911234567890"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white font-semibold rounded-xl hover:bg-green-700 transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>Call Us Now</span>
                </a>
                <a
                  href="mailto:vegobike@gmail.com"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span>Email Support</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default Cart;
